import axios from 'axios';

// Use proxy in both development and production to avoid CORS
const LOCALIZELY_API_BASE = '/api/localizely';

const LOCALIZELY_API_KEY = import.meta.env.VITE_LOCALIZELY_API_KEY;

export interface LocalizelyLanguage {
  langCode: string;
  langName: string;
  strings: number;
  reviewed: number;
  reviewedProgress: number;
}

interface LocalizelyProjectStatus {
  strings: number;
  reviewedProgress: number;
  languages: LocalizelyLanguage[];
}

interface LanguageData {
  languages: LocalizelyLanguage[];
  defaultLanguage: string;
  translations: Record<string, any>;
}

// Helper function to create request config
const createRequestConfig = () => {
  // Always add headers since we need them for API authentication
  return {
    headers: {
      'X-Api-Token': LOCALIZELY_API_KEY,
      'Content-Type': 'application/json'
    }
  };
};

// Get project status with language information
export const getProjectStatus = async (projectId: string): Promise<LocalizelyProjectStatus> => {
  try {
    const response = await axios.get<LocalizelyProjectStatus>(
      `${LOCALIZELY_API_BASE}/projects/${projectId}/status`,
      createRequestConfig()
    );

    return response.data;
  } catch (error: any) {
    // Enhanced error logging for production debugging
    if (!import.meta.env.DEV) {
      console.error('Localizely API Error:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: error?.message,
        url: `${LOCALIZELY_API_BASE}/projects/${projectId}/status`,
        hasApiKey: !!LOCALIZELY_API_KEY
      });
    }
    
    console.warn('Could not fetch project status from Localizely:', error);
    throw error;
  }
};

// Download translations for a specific language
export const downloadTranslations = async (
  projectId: string, 
  langCode: string
): Promise<Record<string, any>> => {
  try {
    const response = await axios.get(
      `${LOCALIZELY_API_BASE}/projects/${projectId}/files/download`,
      {
        params: {
          lang_codes: langCode,
          type: 'json'
        },
        ...createRequestConfig()
      }
    );

    return response.data;
  } catch (error: any) {
    // Enhanced error logging for production debugging
    if (!import.meta.env.DEV) {
      console.error('Localizely Translation Download Error:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        message: error?.message,
        url: `${LOCALIZELY_API_BASE}/projects/${projectId}/files/download`,
        langCode,
        hasApiKey: !!LOCALIZELY_API_KEY
      });
    }
    
    console.warn(`Could not download translations for ${langCode}:`, error);
    throw error;
  }
};

// Fetch complete language data (status + default language translations)
export const fetchLanguageData = async (
  projectId: string, 
  defaultLanguage: string = 'de'
): Promise<LanguageData> => {
  try {
    // First, get the project status to fetch available languages
    const projectStatus = await getProjectStatus(projectId);
    
    // Then, download translations for the default language
    const translations = await downloadTranslations(projectId, defaultLanguage);
    
    return {
      languages: projectStatus.languages,
      defaultLanguage,
      translations
    };
  } catch (error) {
    console.warn('Could not fetch complete language data:', error);
    throw error;
  }
};

 